/**
 * API Configuration
 * 
 * Centralized configuration for API endpoints
 */

// Get API URL from environment variable or default to empty (same-origin)
export const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || '';
};

// API endpoints
export const API_ENDPOINTS = {
  // Teams and accounts
  teams: '/api/teams',
  metrics: '/api/metrics/overall',
  account: (team, env) => `/api/account/${team}/${env}`,
  
  // Vulnerabilities (Zafran integration)
  vulnerabilities: '/api/vulnerabilities',
  vulnerabilityStats: '/api/vulnerabilities/stats',
  vulnerabilityDetail: (id) => `/api/vulnerabilities/${id}`,
  triggerRemediation: (id) => `/api/vulnerabilities/${id}/remediate`,
  remediationStatus: (id) => `/api/remediation/${id}/status`,

  // Batch vulnerability remediation - per-application bucketed (LOW/MEDIUM/HIGH)
  // view, preview-before-trigger plan, and the admin-gated trigger that fans
  // out one PR per bucket.
  batchRemediation: (app) => `/api/batch-remediation/${encodeURIComponent(app)}`,
  batchRemediationPreview: (app) => `/api/batch-remediation/${encodeURIComponent(app)}/preview`,
  batchRemediationTrigger: (app) => `/api/batch-remediation/${encodeURIComponent(app)}/trigger`,
  
  // Alarms
  alarms: (team, env) => `/api/account/${team}/${env}/alarms`,
  alarmMetrics: (service) => `/api/alarm-metrics/${service}`,
  
  // Resources
  resources: (team, env, service) => `/api/account/${team}/${env}/resources/${service}`,
  
  // Recommendations
  recommendations: '/api/recommendations',

  // Top alarms (most frequently triggered, last 30 days)
  topAlarms: '/api/alarms/top',

  // Per-account top alarms (most frequently triggered, last 7 days)
  accountTopAlarms: (team, env) => `/api/account/${team}/${env}/top-alarms`,
  
  // User access
  userAccess: '/api/user/access',
  
  // Integrations (IES pipelines)
  irmaDml: '/api/irma-dml',
  workdayDml: '/api/workday-dml',
  iesData: '/api/ies-data',
  
  // User TODO list
  todos: '/api/todos',
  todosByProject: (projectId) => `/api/todos?projectId=${encodeURIComponent(projectId)}`,
  todo: (taskId) => `/api/todos/${taskId}`,
  todoStatus: (taskId) => `/api/todos/${taskId}/status`,
  todoMove: (taskId) => `/api/todos/${taskId}/move`,

  // TODO projects (task grouping)
  todoProjects: '/api/todos/projects',
  todoProject: (projectId) => `/api/todos/projects/${projectId}`,

  // KoneChat (internal model server chat assistant, async submit/poll)
  koneChatConfig: '/api/konechat/config',
  koneChatChat: '/api/konechat/chat',
  koneChatResult: (jobId) => `/api/konechat/result/${jobId}`,

  // SAP (SAP-only feature) - thin proxy to KONE's SAP MCP server.
  sapConfig: '/api/sap/config',
  sapTools: '/api/sap/tools',
  sapMcp: '/api/sap/mcp',
  // Natural-language entry: { tool, question } -> backend builds the tool args
  // deterministically (no LLM) and runs tools/call.
  sapAsk: '/api/sap/ask',
  // Natural-language chat (Bedrock, async submit/poll - like KoneChat).
  sapChat: '/api/sap/chat',
  sapChatResult: (jobId) => `/api/sap/chat/result/${jobId}`,

  // KPIs / NFRs (Performance, Availability, Scalability) - per team, 3-month history
  kpi: (team) => `/api/kpi/${encodeURIComponent(team)}`,

  // Predictive Monitoring - per team + environment weekly AI (Bedrock)
  // predicted-failure list. Env-scoped so dev and prod show their own account's
  // predictions. Falls back to the team-only path when no env is given.
  predictions: (team, env) =>
    env
      ? `/api/predictions/${encodeURIComponent(team)}/${encodeURIComponent(env)}`
      : `/api/predictions/${encodeURIComponent(team)}`,

  // Idle Resources - per team/env list of AWS resources unused for ~3 months.
  // Bare path returns the global roll-up index across all teams/envs.
  idleResources: (team, env) =>
    `/api/idle-resources/${encodeURIComponent(team)}/${encodeURIComponent(env)}`,
  idleResourcesIndex: '/api/idle-resources',

  // Cost History - monthly cost trend from the S3 historical cache.
  // Bare path returns the overall + per-team roll-up (filtered to allowed teams);
  // the function form returns one account's 6-month monthly trend.
  costHistory: '/api/cost-history',
  costHistoryAccount: (team, env) =>
    `/api/cost-history/${encodeURIComponent(team)}/${encodeURIComponent(env)}`
};

/**
 * Build full API URL
 * @param {string} endpoint - API endpoint path
 * @returns {string} Full API URL
 */
export const buildApiUrl = (endpoint) => {
  return `${getApiUrl()}${endpoint}`;
};

const apiConfig = {
  getApiUrl,
  API_ENDPOINTS,
  buildApiUrl
};

export default apiConfig;
